import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { type Theme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  TextField as DsTextField,
  type TextFieldProps as DsTextFieldProps,
} from '@openx/design-system/components/TextField/TextField.tsx';
import { components as dsComponents } from '@openx/design-system/theme/components.ts';
import {
  type ChangeEvent,
  type ComponentProps,
  cloneElement,
  isValidElement,
  type JSX,
  type Key,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';
import { DebounceField } from '../DebounceField/DebounceField.tsx';
import type { TextFieldProps as CoreTextFieldProps } from '../TextField/TextField.tsx';
import { designSystemOutlinedInput, labelTabMarginSx } from '../theme/designSystemInput.ts';
import { scopeDesignSystem } from '../theme/scopeDesignSystem.ts';

// `MuiIconButton` is scoped so the popup/clear indicators escape the core theme's 28px indicator rule and get the DS
// small size. The input keys are scoped here (not in the core theme) so the DS input styles never reach TextFields
// outside the Autocomplete.
export const autocompleteDesignSystemTheme = (outer: Theme): Theme => {
  const scoped = scopeDesignSystem(['MuiAutocomplete', 'MuiChip', 'MuiIconButton', 'MuiInputBase'])(outer);
  return { ...scoped, components: { ...scoped.components, MuiOutlinedInput: designSystemOutlinedInput } };
};

// Renders the subtree in the scoped DS theme. The outer theme comes from `useTheme`, which falls back to MUI's default
// theme when no ThemeProvider is mounted (unit tests, apps without a root theme). A function `theme` prop would get no
// outer theme there: MUI logs an error and the DS style overrides crash on the missing `theme.breakpoints`.
export const AutocompleteDesignSystemScope = ({ children }: { children: ReactNode }): JSX.Element => {
  const outer = useTheme();
  const theme = useMemo(() => autocompleteDesignSystemTheme(outer), [outer]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

// Same behaviour defaults as the legacy version, minus the legacy popup icon: icons, indicator and chip sizes come from
// the DS theme's `MuiAutocomplete.defaultProps`.
export const designSystemDefaultProps = {
  disabledItemsFocusable: true,
  limitTags: 1,
  multiple: undefined,
};

const dsChipDeleteIcon = dsComponents.MuiChip?.defaultProps?.deleteIcon;

// DS chip, keeping the legacy `data-test` hooks that tests select chips and their delete icons by.
export const designSystemChipProps = (deleteIconDataTest: string): ChipProps =>
  ({
    'data-test': 'chip-button',
    ...(isValidElement(dsChipDeleteIcon) && {
      deleteIcon: cloneElement(dsChipDeleteIcon as ReactElement<Record<string, unknown>>, {
        'data-test': deleteIconDataTest,
      }),
    }),
  }) as ChipProps;

// `textFieldReadOnly` means "pick from the list, no typing". It is set on the native input directly: passing `readOnly`
// through `slotProps` would make the DS TextField render its read-only state (borderless, no dropdown arrow).
// A ref is passed in both states so a true → false change unlocks the input again; the unlocked state still honours
// a `readOnly` that came through `htmlInput` (MUI's <Autocomplete readOnly>). Module-level so the ref stays stable.
const lockedInputRef = (input: HTMLInputElement | null) => {
  if (input) input.readOnly = true;
};
const unlockedInputRef = (input: HTMLInputElement | null) => {
  if (input) input.readOnly = false;
};

type DesignSystemInputOptions = {
  params: AutocompleteRenderInputParams;
  textFieldProps?: CoreTextFieldProps;
  debounced?: boolean;
  onDebounceChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  loadingDataTest?: string;
  textFieldReadOnly?: boolean;
  helperText?: ReactNode;
  error?: boolean;
};

export const renderDesignSystemInput = ({
  params,
  textFieldProps,
  debounced,
  onDebounceChange,
  loading,
  loadingDataTest,
  textFieldReadOnly,
  helperText,
  error,
}: DesignSystemInputOptions): JSX.Element => {
  const { slotProps: paramsSlotProps, ...restParams } = params;
  const { key, ...paramsWithoutKey } = restParams as typeof restParams & { key?: Key };
  // The DS TextField is outlined + medium only and takes `data-test` (it has no `dataTest`).
  const {
    variant: _variant,
    size: _size,
    dataTest,
    slotProps: textFieldSlotProps,
    sx,
    ...restTextFieldProps
  } = textFieldProps ?? {};
  const textFieldInput =
    typeof textFieldSlotProps?.input === 'object'
      ? (textFieldSlotProps.input as { endAdornment?: ReactNode })
      : undefined;
  const textFieldHtmlInput =
    typeof textFieldSlotProps?.htmlInput === 'object' ? textFieldSlotProps.htmlInput : undefined;
  const htmlInput = { ...paramsSlotProps.htmlInput, ...textFieldHtmlInput } as { readOnly?: boolean };

  const inputProps = {
    ...paramsWithoutKey,
    ...(restTextFieldProps as DsTextFieldProps),
    ...(dataTest && { 'data-test': dataTest }),
    sx: [labelTabMarginSx, ...(Array.isArray(sx) ? sx : [sx])],
    onChange: onDebounceChange,
    ...(helperText !== undefined && { helperText }),
    ...(error !== undefined && { error }),
    inputRef: textFieldReadOnly || htmlInput.readOnly ? lockedInputRef : unlockedInputRef,
    slotProps: {
      input: {
        ...paramsSlotProps.input,
        ...textFieldInput,
        endAdornment: (
          <>
            {loading ? (
              <Box sx={{ pr: 1 }}>
                <CircularProgress data-test={loadingDataTest} size={20} />
              </Box>
            ) : null}
            {textFieldInput?.endAdornment}
            {paramsSlotProps.input.endAdornment}
          </>
        ),
      },
      htmlInput,
    },
  } as DsTextFieldProps;

  return debounced ? (
    <DebounceField key={key} {...(inputProps as ComponentProps<typeof DebounceField>)} element={DsTextField} />
  ) : (
    <DsTextField key={key} {...inputProps} />
  );
};
